import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Camera, MessageCircle } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { handleProPlanClick } from "@/lib/checkout";
import { CheckoutModal } from "@/components/checkout/CheckoutModal";


export default function Precos() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  async function handleStarter() {
    setLoading("starter");
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      navigate("/userhome");
    } else {
      navigate("/cadastro");
    }
    setLoading(null);
  }

  async function handlePro() {
    setLoading("pro");
    const loggedIn = await handleProPlanClick(navigate);
    if (loggedIn) setShowCheckout(true);
    setLoading(null);
  }

  function handleEnterprise() {
    window.open(
      "https://wa.me/5511999999999?text=Olá! Tenho interesse no plano Enterprise do AutoCarBid.",
      "_blank"
    );
  }

  return (
    <div className="container mx-auto px-6 py-20 max-w-6xl">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <h1 className="text-4xl md:text-5xl font-bold">
          Planos que <span className="text-primary">Crescem</span> com Você
        </h1>
        <p className="text-muted-foreground mt-3">
          Pague pelo que realmente usa: análise via câmera, IA de danos e precificação baseada na FIPE.
        </p>
      </div>

      <div className="max-w-3xl mx-auto text-sm text-muted-foreground mb-10">
        <div className="flex items-start gap-3">
          <Camera className="w-4 h-4 mt-1 text-primary" />
          <div>
            <p className="font-medium text-foreground">O que é 1 scan?</p>
            <p>
              Uma análise completa de 1 veículo: guia de captura → detecção de danos (IA) → faixa de preço FIPE
              100%–70%. No Pro/Enterprise você pode exportar PDF, salvar na nuvem e integrar por API.
            </p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Starter */}
        <Card className="p-6 flex flex-col">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">Perfeito para começar</p>
            <h3 className="text-2xl font-semibold">Starter</h3>
          </div>
          <p className="text-3xl font-bold mb-4">Grátis</p>
          <ul className="text-sm space-y-2 flex-1">
            <li>✓ 3 scans/mês pelo navegador (câmera do celular ou desktop)</li>
            <li>✓ Estimativa FIPE com faixa 100% → 70%</li>
            <li>✓ Detecção básica de riscos/amassados (IA básica)</li>
            <li>✓ Guia de captura com 8 ângulos</li>
            <li>✓ Histórico local (últimas 5 análises)</li>
            <li>✓ Suporte por e-mail</li>
          </ul>
          <Button className="mt-6" onClick={handleStarter} disabled={loading !== null}>
            {loading === "starter" ? "Processando..." : "Começar Grátis"}
          </Button>
        </Card>

        {/* Pro */}
        <Card className="p-6 border-primary/40 shadow-[0_0_0_2px_rgba(var(--primary-rgb),0.15)] flex flex-col">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">Para compradores frequentes</p>
            <h3 className="text-2xl font-semibold">Pro</h3>
          </div>
          <p className="text-3xl font-bold mb-4">
            R$ 49 <span className="text-base font-medium text-muted-foreground">/mês</span>
          </p>
          <ul className="text-sm space-y-2 flex-1">
            <li>✓ 60 scans/mês (1 scan = 1 veículo)</li>
            <li>✓ Guia de captura avançado com validação de ângulo</li>
            <li>✓ Reconstrução 3D leve + mapa de calor de danos (IA)</li>
            <li>✓ Precificação detalhada por painel + impacto no valor</li>
            <li>✓ Relatórios em PDF com fotos e anotações</li>
            <li>✓ OCR de placa/VIN (quando visível)</li>
            <li>✓ Histórico em nuvem (12 meses)</li>
            <li>✓ Suporte prioritário</li>
          </ul>
          <Button className="mt-6" onClick={handlePro} disabled={loading !== null}>
            {loading === "pro" ? "Processando..." : "Assinar Pro"}
          </Button>
        </Card>

        {/* Enterprise */}
        <Card className="p-6 flex flex-col">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">Para revendedores e empresas</p>
            <h3 className="text-2xl font-semibold">Enterprise</h3>
          </div>
          <p className="text-3xl font-bold mb-4">Personalizado</p>
          <ul className="text-sm space-y-2 flex-1">
            <li>✓ Scans/mês sob demanda (volume)</li>
            <li>✓ Múltiplos usuários e permissões</li>
            <li>✓ API & Webhooks para integrar estoque/ERP/CRM</li>
            <li>✓ Modelos de IA calibrados por frota/segmento</li>
            <li>✓ White-label e domínios personalizados</li>
            <li>✓ SLA, SSO e opções on-prem/edge</li>
            <li>✓ Gerente de conta dedicado</li>
          </ul>
          <Button variant="outline" className="mt-6" onClick={handleEnterprise}>
            Falar com Vendas <MessageCircle className="ml-1 w-4 h-4" />
          </Button>
        </Card>
      </div>

      <CheckoutModal open={showCheckout} onOpenChange={setShowCheckout} />
    </div>
  );
}
